<template>
  <div v-if="isActive" class="guided-tour-overlay">
    <!-- Overlay oscuro simple -->
    <div class="tour-backdrop"></div>
    
    <!-- Spotlight para resaltar el elemento actual -->
    <div 
      v-if="currentStep && spotlightPosition"
      class="tour-spotlight"
      :style="spotlightStyle"
    ></div>
    
    <!-- Tooltip con instrucciones -->
    <div
      v-if="currentStep && tooltipPosition"
      ref="tooltip"
      class="tour-tooltip"
      :style="tooltipStyle"
      :class="tooltipClass"
    >
      <div
        class="tooltip-header tooltip-drag-handle"
        :class="{ 'is-dragging': isDragging }"
        @mousedown="startDrag"
        @touchstart="startDrag"
        title="Arrastra para mover el recuadro"
      >
        <v-icon size="16" class="tooltip-drag-icon">mdi-drag</v-icon>
        <h3 class="tooltip-title">{{ currentStep.title }}</h3>
        <button
          @click="skipTour"
          class="tour-close-btn"
          title="Cerrar tour"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>
      
      <div class="tooltip-content">
        <!-- Paso tipo checklist GS1: ID + tipo + texto oficial + card de prueba -->
        <template v-if="currentStep.checklistText">
          <div class="checklist-meta">
            <span class="checklist-chip checklist-chip-id">{{ currentStep.checklistId }}</span>
            <span class="checklist-chip" :class="typeClass(currentStep.checklistType)">{{ currentStep.checklistType }}</span>
          </div>
          <div class="checklist-source">Checklist de Verificación GS1 — texto oficial</div>
          <blockquote class="checklist-quote">{{ currentStep.checklistText }}</blockquote>
          <div class="prueba-card">
            <div class="prueba-header">
              <v-icon size="15" color="teal-darken-2">mdi-flask-outline</v-icon>
              <span>PRUEBA EN EL SISTEMA</span>
            </div>
            <p class="prueba-text">{{ currentStep.prueba }}</p>
            <div v-if="currentStep.codes && currentStep.codes.length" class="prueba-codes">
              <code v-for="c in currentStep.codes" :key="c" class="prueba-code">{{ c }}</code>
            </div>
          </div>
        </template>
        <p v-else class="tooltip-description">{{ currentStep.description }}</p>
        
        <!-- Imagen o icono si está disponible -->
        <div v-if="currentStep.image" class="tooltip-image">
          <img :src="currentStep.image" :alt="currentStep.title" />
        </div>
        
        <!-- Código de ejemplo si está disponible -->
        <div v-if="currentStep.code" class="tooltip-code">
          <pre><code>{{ currentStep.code }}</code></pre>
        </div>
      </div>
      
      <div class="tooltip-actions">
        <button 
          v-if="currentStepIndex > 0"
          @click="previousStep"
          class="tour-btn tour-btn-secondary"
        >
          <v-icon>mdi-chevron-left</v-icon>
          Anterior
        </button>
        
        <button 
          @click="skipTour"
          class="tour-btn tour-btn-skip"
        >
          Saltar Tour
        </button>
        
        <button 
          v-if="currentStepIndex < totalSteps - 1"
          @click="nextStep"
          class="tour-btn tour-btn-primary"
        >
          Siguiente
          <v-icon>mdi-chevron-right</v-icon>
        </button>
        
        <button 
          v-else
          @click="completeTour"
          class="tour-btn tour-btn-success"
        >
          <v-icon>mdi-check</v-icon>
          Finalizar
        </button>
      </div>
    </div>
    
    <!-- Indicador de progreso -->
    <div 
      v-if="!hasActiveOverlay"
      class="tour-progress"
    >
      <div class="progress-info">
        <div class="step-counter">
          {{ currentStepIndex + 1 }} de {{ totalSteps }}
        </div>
        <span class="progress-text">
          Progreso del Tour
        </span>
      </div>
      <div class="progress-bar">
        <div 
          class="progress-fill"
          :style="{ width: progressPercentage + '%' }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'GuidedTour',
  props: {
    tourSteps: {
      type: Array,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStepIndex: 0,
      spotlightPosition: null,
      tooltipPosition: null,
      resizeObserver: null,
      originalBodyOverflow: null,
      hasActiveOverlay: false,
      overlayObserver: null,
      targetClickListener: null,
      documentClickListener: null,
      isWaitingForAction: false,
      // Arrastre del recuadro del tour
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      dragStart: { x: 0, y: 0 }
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    
    currentStep() {
      return this.tourSteps[this.currentStepIndex] || null
    },
    
    totalSteps() {
      return this.tourSteps.length
    },
    
    progressPercentage() {
      return ((this.currentStepIndex + 1) / this.totalSteps) * 100
    },
    
    spotlightStyle() {
      if (!this.spotlightPosition) return {}
      
      const { top, left, width, height } = this.spotlightPosition
      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
      }
    },
    
    tooltipStyle() {
      if (!this.tooltipPosition) return {}

      return {
        top: `${this.tooltipPosition.top + this.dragOffset.y}px`,
        left: `${this.tooltipPosition.left + this.dragOffset.x}px`,
        transition: this.isDragging ? 'none' : undefined,
      }
    },
    
    tooltipClass() {
      return {
        'tooltip-top': this.tooltipPosition?.placement === 'top',
        'tooltip-bottom': this.tooltipPosition?.placement === 'bottom',
        'tooltip-left': this.tooltipPosition?.placement === 'left',
        'tooltip-right': this.tooltipPosition?.placement === 'right',
        'checklist-step': !!(this.currentStep && this.currentStep.checklistText),
      }
    },
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        this.startTour()
      } else {
        this.stopTour()
      }
    },
    
    currentStepIndex() {
      // Cada paso recalcula su posición ideal: reiniciamos el arrastre manual.
      this.dragOffset = { x: 0, y: 0 }
      this.$nextTick(() => {
        this.updatePositions()
      })
    }
  },
  mounted() {
    if (this.isActive) {
      this.startTour()
    }
  },
  
  beforeUnmount() {
    this.stopTour()
    
    // Limpiar cualquier referencia pendiente
    this.removeTargetClickListener()
    this.removeDragListeners()

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    
    if (this.overlayObserver) {
      this.overlayObserver.disconnect()
      this.overlayObserver = null
    }
  },
  
  methods: {
    // Color del chip según el tipo del punto (colores del checklist oficial)
    typeClass(tipo) {
      const t = String(tipo || '').toLowerCase()
      if (t.startsWith('requerid')) return 'checklist-chip-requerido'
      if (t.startsWith('recomend')) return 'checklist-chip-recomendado'
      // Tour de la 2ª evaluación: puntos corregidos / observaciones resueltas
      if (t.startsWith('corregido') || t.startsWith('observaci')) return 'checklist-chip-corregido'
      return 'checklist-chip-opcional'
    },
    startTour() {
      this.currentStepIndex = 0
      this.setupResizeObserver()
      this.setupOverlayObserver()
      this.$nextTick(() => {
        this.updatePositions()
      })
      
      // Prevenir scroll del body usando clase CSS en lugar de estilo directo
      if (document.body && document.body.classList) {
        try {
          document.body.classList.add('tour-active')
        } catch (error) {
          console.warn('Error adding tour-active class:', error)
          // Fallback al método anterior
          if (document.body) {
            this.originalBodyOverflow = document.body.style.overflow
            document.body.style.overflow = 'hidden'
          }
        }
      }
    },
    
    stopTour() {
      // Limpiar listeners
      this.removeTargetClickListener()
      this.removeDragListeners()
      this.isDragging = false
      this.dragOffset = { x: 0, y: 0 }
      
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      
      if (this.overlayObserver) {
        this.overlayObserver.disconnect()
        this.overlayObserver = null
      }
      
      // Restaurar scroll del body usando clase CSS
      if (document.body && document.body.classList) {
        try {
          document.body.classList.remove('tour-active')
        } catch (error) {
          console.warn('Error removing tour-active class:', error)
          // Fallback al método anterior
          if (document.body) {
            document.body.style.overflow = this.originalBodyOverflow || ''
          }
        }
      }
    },
    
    setupResizeObserver() {
      if (window.ResizeObserver && document.body) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updatePositions()
        })
        this.resizeObserver.observe(document.body)
      }
    },
    
    setupOverlayObserver() {
      if (window.MutationObserver && document.body) {
        this.overlayObserver = new MutationObserver(() => {
          this.checkForActiveOverlays()
        })
        
        this.overlayObserver.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        })
        
        // Verificación inicial
        this.checkForActiveOverlays()
      }
    },
    
    checkForActiveOverlays() {
      // Verificar si hay overlays de Vuetify activos
      const vuetifyOverlays = document.querySelectorAll('.v-overlay--active, .v-dialog--active')
      this.hasActiveOverlay = vuetifyOverlays.length > 0
    },
    
    updatePositions() {
      if (!this.currentStep || !this.currentStep.target) return
      
      // Verificar que el DOM esté disponible
      if (!document || !document.querySelector) {
        console.warn('DOM not available for tour positioning')
        return
      }
      
      const targetElement = this.resolveTarget(this.currentStep.target)
      if (!targetElement) {
        console.warn(`Tour target not found: ${this.currentStep.target}`)
        return
      }
      
      // Verificar que el elemento esté visible.
      // No usar offsetParent: es null en elementos position:fixed (ej. el sidebar)
      // aunque estén perfectamente visibles.
      const visibleRect = targetElement.getBoundingClientRect()
      if (visibleRect.width === 0 && visibleRect.height === 0) {
        console.warn(`Tour target not visible: ${this.currentStep.target}`)
        return
      }
      
      const rect = targetElement.getBoundingClientRect()
      const padding = 8
      
      // Posición del spotlight
      this.spotlightPosition = {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + (padding * 2),
        height: rect.height + (padding * 2)
      }
      
      // Posición del tooltip
      this.calculateTooltipPosition(rect)

      // Reajustar con la altura REAL del tooltip para que los botones nunca
      // queden cortados (sobre todo en móvil, donde la descripción es larga).
      this.clampTooltipToViewport()

      // Configurar listener para clics en el elemento objetivo
      this.$nextTick(() => {
        this.setupTargetClickListener()
      })
      
      // Scroll para asegurar que el elemento esté visible de manera más suave
      try {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
      } catch (error) {
        console.warn('Error scrolling to element:', error)
        // Fallback para navegadores que no soportan scrollIntoView con opciones
        targetElement.scrollIntoView()
      }
    },
    
    calculateTooltipPosition(targetRect) {
      const tooltipWidth = 350
      const tooltipHeight = 200
      const margin = 20
      
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      
      let top, left, placement
      
      // Intentar colocar a la derecha
      if (targetRect.right + margin + tooltipWidth <= viewportWidth) {
        left = targetRect.right + margin
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)
        placement = 'right'
      }
      // Intentar colocar a la izquierda
      else if (targetRect.left - margin - tooltipWidth >= 0) {
        left = targetRect.left - margin - tooltipWidth
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)
        placement = 'left'
      }
      // Intentar colocar arriba
      else if (targetRect.top - margin - tooltipHeight >= 0) {
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)
        top = targetRect.top - margin - tooltipHeight
        placement = 'top'
      }
      // Colocar abajo
      else {
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)
        top = targetRect.bottom + margin
        placement = 'bottom'
      }
      
      // Ajustar si se sale de la pantalla
      left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin))
      top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin))
      
      this.tooltipPosition = { top, left, placement }
    },

    // Reposiciona el tooltip usando su altura/ancho REALES (ya renderizados),
    // dejando un margen inferior seguro para la barra de progreso del tour y la
    // barra de navegación del dispositivo. Evita que los botones queden cortados.
    clampTooltipToViewport() {
      this.$nextTick(() => {
        const el = this.$refs.tooltip
        if (!el || !this.tooltipPosition) return

        const tooltipHeight = el.offsetHeight
        const tooltipWidth = el.offsetWidth
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const isMobile = viewportWidth <= 768
        const margin = 12
        // Reservar espacio abajo: barra de progreso + barra de navegación móvil.
        const safeBottom = isMobile ? 160 : 56

        let top = this.tooltipPosition.top
        let left = this.tooltipPosition.left

        // Si el tooltip se sale por abajo, lo subimos.
        if (top + tooltipHeight > viewportHeight - safeBottom) {
          top = viewportHeight - safeBottom - tooltipHeight
        }
        if (top < margin) top = margin

        // Mantenerlo dentro del ancho.
        if (left + tooltipWidth > viewportWidth - margin) {
          left = viewportWidth - margin - tooltipWidth
        }
        if (left < margin) left = margin

        if (top !== this.tooltipPosition.top || left !== this.tooltipPosition.left) {
          this.tooltipPosition = { ...this.tooltipPosition, top, left }
        }
      })
    },

    nextStep() {
      // Limpiar listener del paso actual
      this.removeTargetClickListener()
      
      if (this.currentStepIndex < this.totalSteps - 1) {
        this.currentStepIndex++
        this.$emit('step-changed', this.currentStepIndex)
        
        // Ejecutar acción del paso automáticamente si existe
        if (this.currentStep.action) {
          try {
            // Pasar el router como parámetro a la acción
            this.currentStep.action(this.$router)
            
            // Esperar a que la pantalla destino pinte el elemento preferido (hasta 3 s);
            // si no aparece, se posiciona igual (queda el respaldo del menú lateral).
            const preferred = String(this.currentStep.target || '').split(',')[0].trim()
            const started = Date.now()
            const tryPosition = () => {
              let el = null
              try { el = preferred ? document.querySelector(preferred) : null } catch (e) { el = null }
              const visible = el && (el.getBoundingClientRect().width > 0 || el.getBoundingClientRect().height > 0)
              if (visible || Date.now() - started > 3000) {
                this.updatePositions()
              } else {
                setTimeout(tryPosition, 150)
              }
            }
            setTimeout(tryPosition, 300)
          } catch (error) {
            console.warn('Error executing step action:', error)
          }
        }
      }
    },
    
    previousStep() {
      // Limpiar listener del paso actual
      this.removeTargetClickListener()
      
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--
        this.$emit('step-changed', this.currentStepIndex)
      }
    },
    
    // --- Arrastre del recuadro del tour ---
    startDrag(event) {
      // No iniciar arrastre al pulsar el botón de cerrar.
      if (event.target.closest('.tour-close-btn')) return

      const point = event.touches ? event.touches[0] : event
      this.isDragging = true
      this.dragStart = {
        x: point.clientX - this.dragOffset.x,
        y: point.clientY - this.dragOffset.y
      }

      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      document.addEventListener('touchmove', this.onDrag, { passive: false })
      document.addEventListener('touchend', this.stopDrag)

      if (event.cancelable) event.preventDefault()
    },

    onDrag(event) {
      if (!this.isDragging) return
      const point = event.touches ? event.touches[0] : event
      this.dragOffset = {
        x: point.clientX - this.dragStart.x,
        y: point.clientY - this.dragStart.y
      }
      if (event.cancelable) event.preventDefault()
    },

    stopDrag() {
      this.isDragging = false
      this.removeDragListeners()
    },

    removeDragListeners() {
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.stopDrag)
    },

    skipTour() {
      this.$emit('tour-skipped')
      this.stopTour()
    },
    
    completeTour() {
      this.$emit('tour-completed')
      this.stopTour()
      
      // Guardar que el usuario completó el tour
      localStorage.setItem(`tour-completed-${this.currentUser?.role}`, 'true')
    },
    
    goToStep(stepIndex) {
      if (stepIndex >= 0 && stepIndex < this.totalSteps) {
        this.currentStepIndex = stepIndex
        this.$emit('step-changed', stepIndex)
      }
    },
    
    handleBackdropClick() {
      // El tour ya no se cancela haciendo clic en el fondo
      // Solo se puede cancelar usando el botón X del tooltip
    },
    
    setupTargetClickListener() {
      if (!this.currentStep || !this.currentStep.target) return

      // El tour SOLO avanza con los botones del tooltip (Siguiente/Finalizar).
      // El avance por click en el elemento destacado quedó opt-in por paso
      // (advanceOnTargetClick: true): con pasos anclados al sidebar, cualquier
      // click en el menú avanzaba el tour por error.
      if (!this.currentStep.advanceOnTargetClick) return

      const targetElement = this.resolveTarget(this.currentStep.target)
      if (!targetElement) return
      
      // Remover listener anterior si existe
      this.removeTargetClickListener()
      
      // Crear nuevo listener
      this.targetClickListener = (event) => {
        // No prevenir la propagación para permitir que el clic funcione normalmente
        //console.log('Target clicked:', event.target)
        this.handleTargetClick(event)
      }
      
      // Agregar listener al elemento objetivo con capture
      targetElement.addEventListener('click', this.targetClickListener, { capture: true, passive: false })
      
      // También agregar listener al documento para capturar clics en el área del spotlight
      document.addEventListener('click', this.documentClickListener = (event) => {
        if (targetElement.contains(event.target)) {
          this.handleTargetClick(event)
        }
      }, { capture: true })
      
      // Marcar que estamos esperando una acción
      this.isWaitingForAction = true
    },
    
    removeTargetClickListener() {
      if (this.targetClickListener && this.currentStep && this.currentStep.target) {
        const targetElement = this.resolveTarget(this.currentStep.target)
        if (targetElement) {
          targetElement.removeEventListener('click', this.targetClickListener, { capture: true })
        }
      }
      
      // Remover listener del documento
      if (this.documentClickListener) {
        document.removeEventListener('click', this.documentClickListener, { capture: true })
        this.documentClickListener = null
      }
      
      this.targetClickListener = null
      this.isWaitingForAction = false
    },
    
    handleTargetClick(event) {
      // Ejecutar la acción del paso si existe
      if (this.currentStep.action) {
        try {
          this.currentStep.action()
        } catch (error) {
          console.warn('Error executing step action:', error)
        }
      }
      
      // Avanzar al siguiente paso después de un breve delay
      setTimeout(() => {
        this.nextStep()
      }, 500)
    },
    
    // `target` puede traer selectores separados por coma en orden de preferencia
    // ("[data-tour=x], [data-tour=sidebar]"): se usa el PRIMERO que exista y sea visible,
    // no el primero en el orden del DOM (querySelector elegiría siempre el sidebar).
    resolveTarget(target) {
      if (!target || !document || !document.querySelector) return null
      const parts = String(target).split(',').map(x => x.trim()).filter(Boolean)
      for (const sel of parts) {
        let el = null
        try { el = document.querySelector(sel) } catch (e) { el = null }
        if (el && el.getBoundingClientRect && (el.getBoundingClientRect().width > 0 || el.getBoundingClientRect().height > 0)) return el
      }
      return null
    },

    waitForNavigation() {
      // Esperar a que la navegación se complete antes de continuar
      return new Promise((resolve) => {
        const checkNavigation = () => {
          // Verificar si la página ha cambiado o si el elemento objetivo está disponible
          if (this.currentStep && this.currentStep.target) {
            const targetElement = this.resolveTarget(this.currentStep.target)
            if (targetElement) {
              resolve()
              return
            }
          }
          
          // Reintentar después de un breve delay
          setTimeout(checkNavigation, 100)
        }
        
        checkNavigation()
      })
    },
  }
}
</script>

<style scoped>
.guided-tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2400; /* Vuetify overlays usan z-index 2000-2300 */
  pointer-events: none;
}

.tour-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  pointer-events: none; /* No bloquear clics */
  z-index: 2400;
}

.tour-spotlight {
  position: absolute;
  background: transparent;
  border-radius: 8px;
  border: 4px solid rgba(33, 150, 243, 0.8);
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 2401;
  box-shadow: 
    0 0 0 9999px rgba(0, 0, 0, 0.3);
}

.tour-tooltip {
  position: absolute;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 350px;
  min-width: 300px;
  pointer-events: all;
  z-index: 2402;
  animation: tooltipFadeIn 0.3s ease;
  /* Nunca más alto que la pantalla (reservando barra de progreso/nav): el
     contenido scrollea, pero header y botones quedan siempre visibles. */
  max-height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
}

.tour-tooltip .tooltip-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* ===== Pasos tipo checklist GS1 (texto oficial + card de prueba) ===== */
.tour-tooltip.checklist-step {
  max-width: 460px;
  min-width: 340px;
}

.checklist-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.checklist-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.3px;
}

.checklist-chip-id {
  background: #1a3a7a; /* azul del encabezado del checklist */
}

.checklist-chip-requerido {
  background: #c00000; /* rojo "Requerido" del documento oficial */
}

.checklist-chip-recomendado {
  background: #e6a817; /* amarillo "Recomendado" */
  color: #3a2c00;
}

.checklist-chip-opcional {
  background: #9e9e9e;
}

.checklist-chip-corregido {
  background: #2e7d32; /* verde: punto corregido tras la 1ª evaluación */
}

.checklist-source {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #90a4ae;
  margin-bottom: 2px;
}

.checklist-quote {
  margin: 0 0 12px 0;
  padding: 8px 12px;
  border-left: 4px solid #1a3a7a;
  background: #f4f6fb;
  border-radius: 0 6px 6px 0;
  font-style: italic;
  font-size: 13.5px;
  line-height: 1.45;
  color: #263238;
  white-space: pre-line; /* respeta el ejemplo multilínea del punto 3.3 */
}

.prueba-card {
  background: #e6f4f2;
  border: 1px solid #b2dfdb;
  border-radius: 8px;
  padding: 10px 12px;
}

.prueba-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: #00695c;
  margin-bottom: 6px;
}

.prueba-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #37474f;
}

.prueba-codes {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.prueba-code {
  display: block;
  background: #ffffff;
  border: 1px dashed #4db6ac;
  border-radius: 6px;
  padding: 6px 8px;
  font-family: 'Courier New', monospace;
  font-size: 12.5px;
  color: #004d40;
  word-break: break-all;
  user-select: all; /* un clic selecciona el código completo para copiar */
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.tooltip-header {
  padding: 20px 20px 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
}

.tooltip-drag-handle {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.tooltip-drag-handle.is-dragging {
  cursor: grabbing;
}

.tooltip-drag-icon {
  color: #bdbdbd;
  margin-top: 2px;
  flex: 0 0 auto;
}

.tooltip-drag-handle:hover .tooltip-drag-icon {
  color: #1976d2;
}

.tooltip-title {
  font-size: 18px;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
  flex: 1;
}

.tour-close-btn {
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.tour-close-btn:hover {
  background: #f5f5f5;
  color: #666;
}

.tooltip-content {
  padding: 16px 20px;
}

.tooltip-description {
  color: #424242;
  line-height: 1.5;
  margin: 0 0 16px 0;
  font-size: 14px;
}

.tooltip-image {
  margin: 16px 0;
  text-align: center;
}

.tooltip-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tooltip-code {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  margin: 16px 0;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  overflow-x: auto;
}

.tooltip-actions {
  padding: 0 20px 20px 20px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.tour-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.tour-btn-primary {
  background: #1976d2;
  color: white;
}

.tour-btn-primary:hover {
  background: #1565c0;
  transform: translateY(-1px);
}

.tour-btn-secondary {
  background: #f5f5f5;
  color: #666;
}

.tour-btn-secondary:hover {
  background: #eeeeee;
}

.tour-btn-skip {
  background: transparent;
  color: #999;
  margin-right: auto;
}

.tour-btn-skip:hover {
  color: #666;
}

.tour-btn-success {
  background: #4caf50;
  color: white;
}

.tour-btn-success:hover {
  background: #45a049;
  transform: translateY(-1px);
}

.tour-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.tour-btn:disabled:hover {
  transform: none !important;
}

.tour-progress {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  pointer-events: all;
  z-index: 2100; /* Reducido para estar debajo de modales de Vuetify */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 280px;
}

.progress-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.step-counter {
  background: #e3f2fd;
  color: #1976d2;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.progress-text {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Tooltips con flechas según posición */
.tooltip-right::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-right-color: white;
}

.tooltip-left::before {
  content: '';
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-left-color: white;
}

.tooltip-top::before {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-top-color: white;
}

.tooltip-bottom::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-bottom-color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .tour-tooltip {
    max-width: 90vw;
    min-width: 280px;
  }
  
  .tooltip-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .tooltip-actions {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .tour-btn {
    flex: 1;
    min-width: 80px;
    justify-content: center;
  }
  
  .tour-progress {
    bottom: 30px;
    left: 16px;
    right: 16px;
    transform: none;
    z-index: 2402;
    min-width: auto;
    padding: 14px 20px;
  }
  
  .progress-info {
    gap: 8px;
  }
  
  .step-counter {
    font-size: 12px;
    padding: 5px 12px;
  }
  
  .progress-text {
    font-size: 11px;
  }
  
  .progress-bar {
    width: 100%;
  }
}

/* Asegurar que el tour no interfiera con otros elementos de Vuetify */
.guided-tour-overlay * {
  box-sizing: border-box;
}

/* Prevenir interferencia con v-overlay */
.guided-tour-overlay .v-overlay {
  z-index: 2403 !important;
}
</style> 